import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import './Pricing.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4310';

const Pricing = ({ user, onUpdateUser, onError, onSuccess }) => {
  const navigate = useNavigate();
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [plans, setPlans] = useState([
    {
      _id: 'plan_starter',
      id: 'plan_starter',
      name: 'Starter',
      credits: 600,
      price: 799,
      badge: 'TRIAL PACK',
      isPopular: false,
      features: [
        '600 Live Credits (~1 hr)',
        'Whisper Audio Transcription',
        'GPT-4o Omniverse AI',
        'Screen Visual OCR',
        'Undetectable Overlay',
        'Email Support'
      ]
    },
    {
      _id: 'plan_pro',
      id: 'plan_pro',
      name: 'Pro',
      credits: 1800,
      price: 1599,
      badge: 'MOST POPULAR',
      isPopular: true,
      features: [
        '1,800 Live Credits (~3 hrs)',
        'Claude 3.5 Sonnet & GPT-4o',
        'System Design & DSA Engine',
        'Hardware Screen Capture',
        'Resume & ATS Analyzer',
        'Voice Mock Interviews',
        'Priority VIP Support'
      ]
    },
    {
      _id: 'plan_elite',
      id: 'plan_elite',
      name: 'Elite',
      credits: 4200,
      price: 3299,
      badge: 'ALL-IN-ONE',
      isPopular: false,
      features: [
        '4,200 Live Credits (~7 hrs)',
        'Claude 3.5, GPT-4o & Gemini',
        'Full-Loop Interview Coverage',
        '1-on-1 AI Interview Sims',
        'Company Question Banks',
        'Auto-Apply & Application Tracker',
        'Priority Career Support'
      ]
    }
  ]);

  // Fetch real-time active pricing plans from backend
  useEffect(() => {
    fetch(`${API_URL}/api/pricing`)
      .then(res => res.json())
      .then(data => {
        if (data && data.success && Array.isArray(data.plans) && data.plans.length > 0) {
          const mapped = data.plans.map(p => {
            const hours = p.duration?.hours || Math.floor(p.credits / 60) || 1;
            const features = p.features && p.features.length > 0 ? p.features : [
              `${Math.round(p.credits).toLocaleString()} Live Credits (~${hours} hr${hours > 1 ? 's' : ''})`,
              'Whisper Audio Transcription',
              'GPT-4o & Multi-Model AI',
              'Screen Visual OCR',
              'Undetectable Overlay',
              p.isPopular ? 'Priority VIP Support' : 'Email Support'
            ];
            return {
              ...p,
              id: p._id,
              features
            };
          });
          setPlans(mapped);
        }
      })
      .catch(err => console.error('[Pricing] Error loading backend plans:', err));
  }, []);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleBuy = async (plan) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const planIdentifier = plan._id || plan.id;
    setSelectedPlan(planIdentifier);
    setIsPurchasing(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        if (onError) onError('Razorpay checkout SDK failed to load. Check internet.');
        setIsPurchasing(false);
        return;
      }

      // 1. Create order
      const res = await fetch(`${API_URL}/api/payments/razorpay/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ planId: planIdentifier, amount: plan.price })
      });

      const data = await res.json();
      if (!data.success || !data.orderId) {
        setIsPurchasing(false);
        if (onError) onError('Failed to create order: ' + (data.message || 'Unknown'));
        return;
      }

      // 2. Open Razorpay
      const options = {
        key: data.keyId || process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_TdtzwCzuRpMxOQ',
        amount: data.amount,
        currency: data.currency || 'INR',
        name: 'CrackIT AI Live Copilot',
        description: `${plan.name} (${Math.round(plan.credits).toLocaleString()} Credits)`,
        image: 'https://cdn-icons-png.flaticon.com/512/4712/4712109.png',
        order_id: data.orderId,
        handler: async function (paymentResponse) {
          try {
            const verifyRes = await fetch(`${API_URL}/api/payments/razorpay/verify-payment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature,
                planId: planIdentifier
              })
            });

            const verifyData = await verifyRes.json();
            setIsPurchasing(false);

            if (verifyData.success) {
              if (onUpdateUser) {
                onUpdateUser(prev => ({
                  ...prev,
                  credits: verifyData.credits
                }));
              }
              if (onSuccess) {
                onSuccess(`Payment Successful! Added ${Math.round(plan.credits).toLocaleString()} credits.`);
              }
              window.dispatchEvent(new CustomEvent('crackit_credits_updated', { detail: { credits: verifyData.credits } }));
              navigate('/dashboard');
            } else {
              if (onError) onError('Verification failed: ' + (verifyData.message || 'Error'));
            }
          } catch (err) {
            console.error('Verify error:', err);
            setIsPurchasing(false);
            if (onError) onError('Verification connection error');
          }
        },
        prefill: {
          name: user ? user.name : '',
          email: user ? user.email : ''
        },
        theme: {
          color: '#1877f2'
        },
        modal: {
          ondismiss: () => setIsPurchasing(false)
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (fail) => {
        if (onError) onError(`Payment failed: ${fail.error.description || 'Declined'}`);
        setIsPurchasing(false);
      });
      rzp.open();

    } catch (err) {
      console.error('Buy error:', err);
      setIsPurchasing(false);
      if (onError) onError('Could not initiate checkout');
    }
  };

  return (
    <section className="coprep-pricing-section" id="pricing">
      <div className="coprep-pricing-container">
        
        {/* Header */}
        <motion.div 
          className="section-header-centered"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <div className="badge-pill">PRICING</div>
          <h2 className="section-main-title">
            Simple <span className="gradient-primary">Pricing</span>
          </h2>
          <p className="section-main-subtitle">
            Pay as you go. No subscriptions. Credits never expire.
          </p>
        </motion.div>

        {/* Pricing Cards Grid */}
        <div className="pricing-cards-grid">
          {plans.map((plan, idx) => (
            <motion.div 
              key={plan._id || plan.id || idx}
              className={`pricing-card glass-card ${plan.isPopular || plan.badge ? 'card-highlighted' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.15 }}
              viewport={{ once: true }}
            >
              {(plan.badge || plan.isPopular) && (
                <div className={`plan-top-badge ${plan.isPopular || plan.badge ? 'badge-glow' : ''}`}>
                  {plan.badge || 'MOST POPULAR'}
                </div>
              )}

              <div className="plan-header-block">
                <h3 className="plan-name-title">{plan.name}</h3>
                <div className="plan-credits-pill">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                  </svg>
                  <strong>{Math.round(plan.credits).toLocaleString()} Copilot Credits</strong>
                </div>
              </div>

              <div className="plan-price-row">
                <span className="price-currency">₹</span>
                <span className="price-amount">{plan.price}</span>
                <span className="price-period">/ one-time</span>
              </div>

              <div className="plan-divider"></div>

              <div className="plan-features-list">
                {plan.features.map((feat, fIdx) => (
                  <div key={fIdx} className="feature-check-item">
                    <span className="check-bullet">✓</span>
                    <span className="feature-text">{feat}</span>
                  </div>
                ))}
              </div>

              <button 
                className={`plan-buy-btn ${plan.isPopular ? 'btn-primary-highlight' : 'btn-secondary-plan'}`}
                onClick={() => handleBuy(plan)}
                disabled={isPurchasing}
              >
                {isPurchasing && selectedPlan === (plan._id || plan.id) ? 'Opening Razorpay...' : `Get ${Math.round(plan.credits).toLocaleString()} Credits →`}
              </button>
            </motion.div>
          ))}
        </div>

        {/* Payment Security Trust Banner */}
        <div className="pricing-trust-banner">
          <div className="trust-item">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <span>256-Bit SSL Encrypted Checkout</span>
          </div>
          <div className="trust-item">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect width="20" height="14" x="2" y="5" rx="2"/>
              <line x1="2" y1="10" x2="22" y2="10"/>
            </svg>
            <span>UPI, Google Pay, Cards, NetBanking (Razorpay)</span>
          </div>
          <div className="trust-item">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
            <span>Instant Balance Top-Up</span>
          </div>
        </div>

        {/* Strict No-Refund Notice */}
        <div className="pricing-refund-notice">
          <span>* All credit pack purchases are final and subject to our <Link to="/refund-policy">Strict No-Refund Policy</Link>. Digital goods are credited instantly upon payment.</span>
        </div>

      </div>
    </section>
  );
};

export default Pricing;
